import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import useAuth from '@/hooks/useAuth';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';

interface AdminReadStateRow {
  conversation_id: string;
  last_read_at: string;
}

interface UseAdminReadStateResult {
  readMap: Record<string, string>;
  loading: boolean;
  error: string | null;
  markRead: (conversationId: string, at?: string) => Promise<void>;
  refresh: () => Promise<void>;
}

// Cap initial read-state fetch so long-tenured admins with thousands
// of stale rows don't pull them all on every page load.
const READ_STATE_FETCH_LIMIT = 500;

export function useAdminReadState(): UseAdminReadStateResult {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const { currentAdmin } = useAuth();
  const adminUserId = currentAdmin?.user?.id ?? null;
  const [readMap, setReadMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!client || !ready || !adminUserId) {
      setReadMap({});
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await client
        .from('admin_read_state')
        .select('conversation_id, last_read_at')
        .eq('admin_user_id', Number(adminUserId))
        .order('last_read_at', { ascending: false })
        .limit(READ_STATE_FETCH_LIMIT);
      if (err) throw err;
      const map: Record<string, string> = {};
      for (const row of (data as AdminReadStateRow[]) ?? []) {
        map[row.conversation_id] = row.last_read_at;
      }
      setReadMap(map);
    } catch (err: unknown) {
      Sentry.captureException(err, {
        tags: { feature: 'comms', action: 'fetch_admin_read_state' },
      });
      setError((err as Error)?.message ?? 'Failed to load read state');
    } finally {
      setLoading(false);
    }
  }, [client, ready, adminUserId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markRead = useCallback(
    async (conversationId: string, at?: string) => {
      if (!client || !ready || !adminUserId) return;
      const lastReadAt = at ?? new Date().toISOString();
      // Optimistic: set state BEFORE the upsert so the unread dot
      // doesn't flicker back on if a realtime refresh races with us.
      let previous: string | undefined;
      setReadMap((prev) => {
        previous = prev[conversationId];
        return { ...prev, [conversationId]: lastReadAt };
      });
      try {
        const { error: err } = await client.from('admin_read_state').upsert(
          {
            admin_user_id: Number(adminUserId),
            conversation_id: conversationId,
            last_read_at: lastReadAt,
          },
          { onConflict: 'admin_user_id,conversation_id' },
        );
        if (err) throw err;
        window.dispatchEvent(new Event('comms:read'));
      } catch (err: unknown) {
        // Revert optimistic update on failure.
        setReadMap((prev) => {
          const next = { ...prev };
          if (previous === undefined) delete next[conversationId];
          else next[conversationId] = previous;
          return next;
        });
        Sentry.captureException(err, {
          tags: { feature: 'comms', action: 'mark_admin_read' },
          extra: { conversation_id: conversationId },
        });
      }
    },
    [client, ready, adminUserId],
  );

  return { readMap, loading, error, markRead, refresh };
}
